import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsightsIcon from "@mui/icons-material/Insights";
import JBrowseService from "../services/jbrowse.service";
import socketIOClient from "socket.io-client";
import socketUrl from "../common/GetSocket";

const ENDPOINT = socketUrl();

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  maxHeight: "90vh",
  overflowY: "auto",
  width: "80%",
  p: 4,
};

const FeatureTableModal = (props) => {
  const {
    gffs,
    featureModalIsOpen,
    setFeatureModalIsOpen,
    setAccessVariantIsOpen,
    navigateToLocation,
    location,
    setLoading,
    currentUser,
    selectedCrop,
    aiLoading,
    setAiLoading,
  } = props;

  const [featureForm, setFeatureForm] = useState({
    gff: "",
    Term: "",
    Gene: "",
  });
  const [spinner, setSpinner ] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [featureTableData, setFeatureTableData] = useState([]);
  const [resultIsOpen, setResultIsOpen] = useState(false);
  const [isParagraphVisible, setIsParagraphVisible] = useState(false);
  const [featureParagraph, setFeatureParagraph] = useState("");
  const [gffWarning, setGffWarning] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(featureTableData.length / itemsPerPage);
  const displayedItems = featureTableData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleChange = (e) => {
    setFeatureForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onBuildFeatureTable = () => {
    setSpinner(true);
    setAiLoading(true);

    const formCopy = {
      ...featureForm,
      positon: location,
      crop: selectedCrop,
      user: currentUser.email,
      company: currentUser.DirectoryName,
    };

    if (formCopy.gff === "") {
      setSpinner(false);
      setAiLoading(false);
      setGffWarning(true);
      setTimeout(() => {
        setGffWarning(false);
      }, 3000);
    } else {
      JBrowseService.buildFeatureTable(formCopy).then(
        (response) => {
          setSpinner(false);
          if (response.status === 0) {
            setSuccessMessage(response.message);
            setTimeout(() => {
              setSuccessMessage("");
            }, 3000);
            setFeatureTableData(response.result);
            setResultIsOpen(true);
          } else {
            setErrorMessage(response.message);
            setTimeout(() => {
              setErrorMessage("");
            }, 3000);
          }
        },
        (error) => {
          setSpinner(false);
          setErrorMessage(error.message);
          setTimeout(() => {
            setErrorMessage("");
          }, 3000);
        }
      );
    }
  };

  const onGoBackFeature = () => {
    setFeatureModalIsOpen(false);
    setTimeout(() => {
      setAccessVariantIsOpen(true);
    }, 100);
  };

  const closeModal = () => {
    setFeatureModalIsOpen(false);
    setResultIsOpen(false);
  };

  const onGoBackResultsFeature = () => {
    setResultIsOpen(false);
    setFeatureTableData([]);
  };

  const jumpToLocation = (loc) => {
    setLoading(true);
    navigateToLocation(loc);
    setTimeout(() => {
      setFeatureModalIsOpen(false);
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    if (featureModalIsOpen) {
      setOpacity(100);
    } else {
      setOpacity(0);
    }
  }, [featureModalIsOpen]);

  useEffect(() => {
    const socket = socketIOClient(ENDPOINT);
    socket.on("featureTable", (response) => {
      if (currentUser.email === response.user) {
        setAiLoading(false);
        setFeatureParagraph(response.openAi);
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [currentUser.email]);

  return (
    <>
      {/* FIRST MODAL (Form) */}
      <Modal open={featureModalIsOpen && !resultIsOpen} onClose={closeModal}>
        <Box sx={modalStyle} className="transition-opacity duration-500" style={{ opacity }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              Feature Table
            </Typography>
            <IconButton onClick={closeModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Success/Error/Warning Alerts */}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Success!</AlertTitle>
              Successful Retrieval
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Error!</AlertTitle>
              {errorMessage}
            </Alert>
          )}
          {gffWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Warning!</AlertTitle>
              GFF Warning, Select a GFF and try again.
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="gff-select-label">Select a GFF</InputLabel>
            <Select
              labelId="gff-select-label"
              id="gff-select"
              label="Select a GFF"
              name="gff"
              value={featureForm.gff}
              onChange={handleChange}
            >
              <MenuItem value="" disabled>
                Choose your option
              </MenuItem>
              {gffs.map((x) => {
                const fileName = x.fileName.split("/").pop();
                return (
                  <MenuItem key={x.fileName} value={x.fileName}>
                    {fileName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Search Term (optional)"
            name="Term"
            value={featureForm.Term}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Search Gene (optional)"
            name="Gene"
            value={featureForm.Gene}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {spinner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div className="lds-ellipsis">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                Awaiting response from Verinomics servers...
              </Typography>
            </div>
          )}

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button
              onClick={onGoBackFeature}
              variant="contained"
              className="bg-secondary"
              sx={{ mr: 1 }}
            >
              Go Back
            </Button>

            <Box>
              <Button
                onClick={onBuildFeatureTable}
                variant="contained"
                className="bg-green-400 hover:bg-secondary"
                sx={{ mr: 2 }}
              >
                Build
              </Button>
              <Button onClick={closeModal} variant="contained" className="bg-secondary">
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* SECOND MODAL (Results) */}
      <Modal open={resultIsOpen} onClose={closeModal}>
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" component="h2" fontWeight="bold">
                Feature Table
              </Typography>
              <Typography variant="subtitle1" ml={2}>
                - {featureTableData.length.toLocaleString()} hits, {itemsPerPage} per page
              </Typography>
            </Box>

            <Box display="flex" alignItems="center">
              <Button
                className="bg-green-600 hover:bg-secondary"
                variant="contained"
                onClick={() => setIsParagraphVisible(!isParagraphVisible)}
                sx={{ mr: 2 }}
              >
                {aiLoading ? (
                  <Box display="flex" alignItems="center">
                    <svg
                      className="animate-spin"
                      style={{ width: 24, height: 24, marginRight: 8 }}
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4
                                  zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
                                  5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Waiting for AI Server...
                  </Box>
                ) : (
                  <>
                    <InsightsIcon sx={{ mr: 1 }} />
                    {isParagraphVisible ? "Hide" : "Show"} AI Analysis
                  </>
                )}
              </Button>
              <IconButton onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {isParagraphVisible && (
            <Box mt={2} mb={2}>
              {featureParagraph === "" ? (
                <Alert severity="info">
                  <AlertTitle>Waiting for Server Response</AlertTitle>
                  We're currently waiting for a response from the AI server. This should take less
                  than a minute, please hang tight!
                </Alert>
              ) : (
                <Typography sx={{ whiteSpace: "pre-line", mt: 2 }}>{featureParagraph}</Typography>
              )}
            </Box>
          )}

          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow className="bg-secondary">
                  <TableCell sx={{ color: "black", fontWeight: "bold" }}>Jump</TableCell>
                  <TableCell sx={{ color: "black", fontWeight: "bold" }}>Contig</TableCell>
                  <TableCell sx={{ color: "black", fontWeight: "bold" }}>Start</TableCell>
                  <TableCell sx={{ color: "black", fontWeight: "bold" }}>End</TableCell>
                  <TableCell sx={{ color: "black", fontWeight: "bold" }}>ID</TableCell>
                  <TableCell sx={{ color: "black", fontWeight: "bold" }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Button
                        onClick={() => jumpToLocation(`${item.Contig}:${item.Start}-${item.End}`)}
                        variant="contained"
                        className="bg-green-600 hover:bg-secondary"
                      >
                        Jump
                      </Button>
                    </TableCell>
                    <TableCell>{item.Contig}</TableCell>
                    <TableCell>{item.Start}</TableCell>
                    <TableCell>{item.End}</TableCell>
                    <TableCell>{item.ID}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.Note}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <Button
              onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
              variant="contained"
              className="bg-secondary"
              sx={{ mr: 2 }}
              disabled={currentPage === 1}
            >
              Prev
            </Button>
            <Typography>
              Page {currentPage} of {totalPages}
            </Typography>
            <Button
              onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
              variant="contained"
              className="bg-secondary"
              sx={{ ml: 2 }}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Box>

          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button onClick={onGoBackResultsFeature} variant="contained" className="bg-secondary">
              Go Back
            </Button>
            <Button onClick={closeModal} variant="contained" className="bg-secondary">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FeatureTableModal;
